import {
  getSortedDrawUnit,
  nextOverlayPaintSerial,
  paintDrawUnitList,
} from './drawUnit';
import { createOverlayHitRegistry } from './OverlayHitRegistry';
import { createOverlayPointerDispatcher } from './OverlayPointerDispatcher';
import { syncCanvasSize } from './syncCanvasSize';
import {
  type OverlayDrawContext,
  type OverlayHitTarget,
  type OverlayHostBinding,
  type OverlayHoverState,
  type OverlayLayerEntry,
  type OverlayLayerOption,
  type OverlayPaintContext,
  type OverlayPlane,
} from './type';

/**
 * 평면별 "직전 프레임에 그린 게 있었는지" 상태.
 * 빈 평면의 clear 를 건너뛰되, 콘텐츠 있음 → 없음 전이에서 마지막 한 번은 비우기 위해 필요하다.
 */
type PlanePaintedState = Record<OverlayPlane, boolean>;

/**
 * 평면 상태 초기값. 엔진 인스턴스마다 새로 만들며, "직전에 그린 게 있다" 로 시작해
 * 첫 프레임이 비어 있어도 clear 를 한 번은 수행하게 한다(재마운트 시 잔상 방지).
 */
const createPlanePaintedState = (): PlanePaintedState => ({
  top: true,
  underLabel: true,
});

type PaintPlaneOption = {
  baseContext: Omit<
    OverlayDrawContext,
    'bufH' | 'bufW' | 'canvas' | 'ctx' | 'paintSerial'
  >;
  canvas: Nullish<HTMLCanvasElement>;
  layerList: OverlayLayerEntry[];
  paintedState: PlanePaintedState;
  plane: OverlayPlane;
  recordHitTarget: (layerId: string, target: OverlayHitTarget) => void;
};

/**
 * 한 평면(canvas)을 clear 후 해당 plane 의 레이어만 정렬해 paint 한다.
 *
 * 그릴 draw unit 이 하나도 없는 평면은 전체 화면 clear 자체를 건너뛴다 (VPF-3515).
 * 2434x1241 전체 clearRect 는 실측에서 오버레이 프레임 예산의 61.7% 를 차지했고,
 * 평상시 `underLabel` 평면은 아무것도 그리지 않으므로 그 비용이 전부 낭비다.
 * 특정 평면 전용 처리가 아니라 "비어 있는 평면" 일반 규칙이라 top 평면과
 * 앞으로 추가될 평면에도 자동으로 적용된다.
 */
const paintPlane = ({
  baseContext,
  canvas,
  layerList,
  paintedState,
  plane,
  recordHitTarget,
}: PaintPlaneOption) => {
  if (!canvas) return;

  const { pixelRatio } = baseContext;
  // 크기 동기화는 비어 있어도 건너뛰지 않는다 — 버퍼 크기 변경 자체가 캔버스를 비우므로
  // 뷰포트가 바뀌면 잔상 없이 최신 크기를 유지해야 한다.
  const { bufH, bufW } = syncCanvasSize(
    canvas,
    baseContext.cssH,
    baseContext.cssW,
    pixelRatio,
  );
  const ctx = canvas.getContext('2d', { alpha: true });
  if (!ctx) return;

  const drawContext: OverlayDrawContext = {
    ...baseContext,
    bufH,
    bufW,
    canvas,
    ctx,
    paintSerial: nextOverlayPaintSerial(),
  };

  // collect 콜백이 measureText 등으로 ctx 를 쓸 수 있어, 수집 시점의 변환 상태가
  // paint 시점과 같아야 한다. clear 보다 먼저 호출하므로 여기서 먼저 DPR 변환을 건다.
  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  const unitList = getSortedDrawUnit(layerList, drawContext, plane);

  if (unitList.length === 0) {
    // 이미 비어 있던 평면이면 clear 도 생략한다.
    if (!paintedState[plane]) return;
    // 직전 프레임의 그림이 남지 않도록 마지막으로 한 번만 비운다.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, bufW, bufH);
    paintedState[plane] = false;
    return;
  }

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, bufW, bufH);
  // 픽셀을 건드리기 시작했으면 플래그부터 세운다. paint 루프 뒤에 세우면 unit.paint 가
  // throw 했을 때 부분 그림 + "비어 있음" 플래그가 남아 이후 clear 가 영구 생략된다.
  paintedState[plane] = true;
  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

  // 판정 대상은 실제 paint 중에만 기록한다. 지금 paint 중인 단위의 레이어 칸에 쌓습니다.
  let currentLayerId = '';
  const paintContext: OverlayPaintContext = {
    ...drawContext,
    addHitTarget: target => recordHitTarget(currentLayerId, target),
  };
  paintDrawUnitList(unitList, paintContext, unit => {
    currentLayerId = unit.layerId;
  });
};

export type OverlayEngineOption = {
  /** 레이어 보관 맵을 외부와 공유합니다. 생략하면 엔진이 새로 만듭니다. */
  layerMap?: Map<string, OverlayLayerEntry>;
};

export type OverlayLayerHandle = {
  unregister: () => void;
  update: (option: OverlayLayerOption) => void;
};

/**
 * 오버레이 엔진. 레이어 레지스트리·도장·판정 데이터·호버 상태·dirty 세대·그리기 스케줄 판정을
 * 인스턴스에 보관하므로 Provider(호스트)마다 독립입니다. 특정 앱 모듈에 의존하지 않고
 * 필요한 외부 정보는 모두 `OverlayHostBinding` 으로 받습니다.
 */
export const createOverlayEngine = (
  binding: OverlayHostBinding,
  option: OverlayEngineOption = {},
) => {
  const layerMap = option.layerMap ?? new Map<string, OverlayLayerEntry>();
  const hitRegistry = createOverlayHitRegistry();
  const planePaintedState = createPlanePaintedState();

  let dirtyGeneration = 0;
  let layerVersion = 0;
  let orderCounter = 0;
  let paintSerial = 0;
  let sortedCache = {
    list: [] as OverlayLayerEntry[],
    size: -1,
    version: -1,
  };

  /** priority 높은 순, 같으면 나중에 등록된 순. 판정 순회용입니다. */
  const getLayerList = () => {
    if (
      sortedCache.version !== layerVersion ||
      sortedCache.size !== layerMap.size
    ) {
      sortedCache = {
        list: [...layerMap.values()].sort(
          (a, b) => b.priority - a.priority || b._order - a._order,
        ),
        size: layerMap.size,
        version: layerVersion,
      };
    }
    return sortedCache.list;
  };

  const dispatcher = createOverlayPointerDispatcher({
    binding,
    getLayerList,
    getLayerMap: () => layerMap,
    getLayerVersion: () => layerVersion,
    getPaintSerial: () => paintSerial,
    registry: hitRegistry,
    requestRedraw: () => invalidate(),
  });

  /** 다음 프레임에서 다시 그려야 함을 표시합니다(프레임 요청 없이 세대만 올림). */
  const markDirty = () => {
    dirtyGeneration += 1;
  };

  /** 세대를 올리고 캔버스 프레임을 1회 요청합니다. */
  const invalidate = () => {
    markDirty();
    binding.invalidate();
  };

  /**
   * 레이어를 등록하거나 갱신합니다. 같은 객체를 다시 넣으면 아무것도 하지 않아 dirty 를 요청하지
   * 않습니다(실제로 바뀐 경우에만 무효화).
   */
  const upsertLayer = (entry: OverlayLayerEntry) => {
    if (layerMap.get(entry.id) === entry) return;
    dispatcher.handleLayerChange(entry.id, entry);
    layerMap.set(entry.id, entry);
    layerVersion += 1;
    // 그리지 않는 레이어의 직전 판정 데이터가 남지 않게 합니다.
    if (!entry.enabled) hitRegistry.clearLayer(entry.id);
    dispatcher.syncParticipation();
    invalidate();
  };

  const removeLayer = (id: string) => {
    if (!layerMap.has(id)) return;
    dispatcher.handleLayerChange(id, null);
    layerMap.delete(id);
    layerVersion += 1;
    hitRegistry.clearLayer(id);
    dispatcher.syncParticipation();
    invalidate();
  };

  const registerLayer = (
    layerOption: OverlayLayerOption,
  ): OverlayLayerHandle => {
    orderCounter += 1;
    const order = orderCounter;
    let current: OverlayLayerEntry = { ...layerOption, _order: order };
    upsertLayer(current);
    return {
      unregister: () => {
        // 같은 id 로 다시 등록된 다른 레이어를 지우지 않습니다.
        if (layerMap.get(current.id) === current) removeLayer(current.id);
      },
      update: next => {
        const previous = current;
        current = { ...next, _order: order };
        if (
          previous.id !== current.id &&
          layerMap.get(previous.id) === previous
        ) {
          removeLayer(previous.id);
        }
        upsertLayer(current);
      },
    };
  };

  /**
   * 모든 평면을 한 번 도장하고 판정 데이터를 확정합니다. 카메라가 없거나 뷰포트가 0 이면
   * 낡은 판정 데이터를 비웁니다. 도장이 throw 하면 판정 데이터를 비운 채 예외를 그대로 던집니다.
   */
  const drawFrame = () => {
    const camera = binding.getCamera();
    const viewport = binding.getViewport();
    if (!camera || viewport.cssW === 0 || viewport.cssH === 0) {
      hitRegistry.clearAll();
      paintSerial += 1;
      dispatcher.handlePaintCommitted();
      return;
    }

    const layerList = [...layerMap.values()];
    const baseContext = {
      camera,
      cssH: viewport.cssH,
      cssW: viewport.cssW,
      halfH: viewport.cssH / 2,
      halfW: viewport.cssW / 2,
      pixelRatio: window.devicePixelRatio || 1,
      timeMs: performance.now(),
      viewport: {
        height: viewport.cssH,
        left: viewport.left ?? 0,
        top: viewport.top ?? 0,
        width: viewport.cssW,
      },
    };
    const recordHitTarget = (layerId: string, target: OverlayHitTarget) =>
      hitRegistry.add(layerId, target);

    hitRegistry.beginPaint();
    try {
      paintPlane({
        baseContext,
        canvas: binding.getOverlayCanvas('top'),
        layerList,
        paintedState: planePaintedState,
        plane: 'top',
        recordHitTarget,
      });
      paintPlane({
        baseContext,
        canvas: binding.getOverlayCanvas('underLabel'),
        layerList,
        paintedState: planePaintedState,
        plane: 'underLabel',
        recordHitTarget,
      });
    } catch (error: unknown) {
      hitRegistry.clearAll();
      // 판정 데이터가 비었으므로 도장 세대를 올려, 정지한 포인터의 낡은 호버·커서를 다시 판정해 해제합니다.
      paintSerial += 1;
      dispatcher.handlePaintCommitted();
      throw error;
    }
    hitRegistry.commitPaint();
    paintSerial += 1;
    dispatcher.handlePaintCommitted();
  };

  const handleCanvasChange = () => {
    dispatcher.handleCanvasChange();
    hitRegistry.clearAll();
    invalidate();
  };

  /** 호스트 root 에 포인터 리스너를 붙이고 캔버스 교체를 구독합니다. 반환 함수가 모두 정리합니다. */
  const attachHost = (root: HTMLElement) => {
    planePaintedState.top = true;
    planePaintedState.underLabel = true;
    // 맵이 엔진 밖에서 채워졌을 수 있으므로 정렬 캐시를 새로 만듭니다.
    layerVersion += 1;
    dispatcher.attach(root);
    const unsubscribe = binding.subscribeCanvas(handleCanvasChange);
    return () => {
      unsubscribe();
      dispatcher.detach();
    };
  };

  return {
    attachHost,
    drawFrame,
    getDirtyGeneration: () => dirtyGeneration,
    getHover: (): Nullish<OverlayHoverState> => dispatcher.getHover(),
    hitRegistry,
    invalidate,
    layerMap,
    markDirty,
    registerLayer,
    removeLayer,
    upsertLayer,
  };
};

export type OverlayEngine = ReturnType<typeof createOverlayEngine>;
